import { Button, Checkbox, Col, Form, Input, message, Row, Select, Space, Table } from "antd";
import { useEffect, useState, useRef } from "react";
import { serverFetch } from "@server";
import { CloseCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import TakePhoto from "@controls/take-photo/take-photo";
import UploadPhoto from "@controls/take-photo/upload-photo";
import "./room.css";
import { LogMonitor } from "@controls";
import { useSelector } from "react-redux";
import { licenseModuleAccessControl } from "@/extensions";

const normalizeIdx = (val) => {
  if (val === undefined || val === null || val === "") return val;
  const num = parseInt(val, 10);
  return Number.isNaN(num) ? val : num;
};

const Room = ({ id, onClose }) => {
  const [item, setItem] = useState({ vid: 1, active: true, markVid: 0, cnts: [], ctrls: [] });
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [calendars, setCalendars] = useState([]);
  const [products, setProducts] = useState([]);
  const [companyCtrls, setCompanyCtrls] = useState([]);
  const [hasSkdLicense, setHasSkdLicense] = useState(false);
  const imageRef = useRef(null);
  const [takePhoto, setTakePhoto] = useState(false);
  const [uploadPhoto, setUploadPhoto] = useState(false);
  const user = useSelector((s) => s.user);

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`room/${id}`)
        .then(data => {
          setLoading(false);
          setItem({ ...data, ctrls: data.ctrls || [] });
          form.setFieldsValue(data);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
    }
    else form.setFieldsValue(item);
    serverFetch("calendar")
      .then(data => setCalendars(data.map(i => ({value: i.id, label: i.name}))))
      .catch(e => message.error(e.userMessage));
    
    serverFetch("product/forsale12")
      .then(data => setProducts(data.map(i => ({ label: i.name, value: i.id, duration: i.duration}))))
      .catch(e => message.error(e.userMessage));

    if (user?.companyId && hasSkdLicense) {
      serverFetch(`CompanyCtrl?companyId=${user.companyId}`)
        .then(data => setCompanyCtrls(data.map(i => ({ label: i.name || i.sn, value: i.id }))))
        .catch(e => message.error(e.userMessage));
    }
  }

  useEffect(() => {
    serverFetch("company/current")
      .then((data) => setHasSkdLicense(licenseModuleAccessControl(data?.activeLicense?.activeModules)))
      .catch(e => message.error(e.userMessage));
  }, []);

  useEffect(getData, [id, form, user?.companyId, hasSkdLicense])

  const onValuesChange = (value) => {
    const updated = { ...value };
    if (Object.prototype.hasOwnProperty.call(updated, "idx")) {
      updated.idx = normalizeIdx(updated.idx);
    }
    setItem({ ...item, ...updated });
  };

  const onAddProduct = productId => setItem({...item, cnts: [...item.cnts, { roomId: id, productId }]})
  const onDelProduct = productId => setItem({...item, cnts: item.cnts.filter(i => i.productId !== productId)})

  const ctrlVidOptions = [
    { label: "Авто", value: 0 },
    { label: "Вход", value: 1 },
    { label: "Выход", value: -1 },
    { label: "Выход с изъятием", value: -2 }
  ];

  const onAddCtrl = () => {
    const newCtrl = { id: 0, roomId: id, companyControllerId: null, vid: 0, active: true };
    setItem({...item, ctrls: [...(item.ctrls || []), newCtrl]});
  }

  const onDelCtrl = (index) => {
    const newCtrls = [...(item.ctrls || [])];
    newCtrls.splice(index, 1);
    setItem({...item, ctrls: newCtrls});
  }

  const onChangeCtrl = (index, field, value) => {
    const newCtrls = [...(item.ctrls || [])];
    newCtrls[index] = { ...newCtrls[index], [field]: value };
    setItem({...item, ctrls: newCtrls});
  } 

  const onClosePhoto = (refresh) => {
    setTakePhoto(false);
    refresh && getData();
  }
  const onCloseUpload = (refresh) => {
    setUploadPhoto(false);
    refresh && getData();
  }

  const onSavePhoto = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    serverFetch(`room/photo/${id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };
  const onDeletePhoto = () => {
    const formData = new FormData();
    serverFetch(`room/photo/${id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onSave = () => {
    setLoading(true)
    const payload = { ...item, idx: normalizeIdx(item.idx) };
    serverFetch("room", { method: id ? "PUT" : "POST", bodyData: payload })
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  }

  const onCancel = () => onClose()

  const availableProducts = products.filter(p => !item.cnts.some(cnt => cnt.productId === p.value));

  const divProducts = <>
    <Form.Item label="Услуги">
      <Select options={availableProducts} placeholder="Добавить услугу" onChange={productId => onAddProduct(productId)} value={null}/>
    </Form.Item>
    {item.cnts.map(cnt => <Form.Item label=" " key={cnt.productId}>
      <Select open={false} options={products.filter(p => p.value === cnt.productId)} value={cnt.productId} allowClear onClear={() => onDelProduct(cnt.productId)}/>      
    </Form.Item>)}
  </>

  const ctrlsColumns = [
    {
      title: "Контроллер",
      dataIndex: "companyControllerId",
      width: 200,
      render: (value, record, index) => (
        <Select
          value={value}
          onChange={(val) => onChangeCtrl(index, "companyControllerId", val)}
          options={companyCtrls}
          placeholder="Выберите контроллер"
          allowClear
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Вид",
      dataIndex: "vid",
      width: 120,
      render: (value, record, index) => <Select value={value} onChange={(val) => onChangeCtrl(index, "vid", val)} options={ctrlVidOptions} style={{ width: "100%" }} />,
    },
    {
      title: "Активно",
      dataIndex: "active",
      width: 80,
      align: "center",
      render: (value, record, index) => <Checkbox checked={value} onChange={(e) => onChangeCtrl(index, "active", e.target.checked)} />,
    },
    {
      title: "",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record, index) => <DeleteOutlined  style={{ color: "red", cursor: "pointer", fontSize: 16 }}  onClick={() => onDelCtrl(index)} />,
    },
  ];

  const divCtrls = (<>
    <Form.Item label={<Button onClick={onAddCtrl} size="small">Добавить</Button>} labelCol={8}>Оборудование</Form.Item>
    <Form.Item>
      <Table dataSource={item.ctrls || []} columns={ctrlsColumns} rowKey={(record, index) => index} size="small" pagination={false} style={{ marginTop: 8 }} />
    </Form.Item>
    </>
  )

  return<div className="sport-modal">
    <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose(false)}>
      <div className="sport-modal-content" onMouseDown={e => e.stopPropagation()}>
        <div className="sport-modal-title"><span>Зал</span><CloseCircleOutlined className="sport-section-title-close" onClick={onCancel}/></div>
        <Form form={form} onValuesChange={onValuesChange} labelCol={{ span: 4 }} wrapperCol={{ span: 24 }} colon={false} disabled={loading}>
          <Form.Item name="active" valuePropName="checked" label=" "><Checkbox>Активный</Checkbox></Form.Item>
          <Form.Item>
            <Row gutter={4}>
              <Col span={16}>
                <Form.Item label="Порядок" name="idx" labelCol={{span: 6}}><Input /></Form.Item>
                <Form.Item label="Наимен." name="name" labelCol={{span: 6}}><Input /></Form.Item>
                <Form.Item label="График" name="calendarId" labelCol={{span: 6}}><Select options={calendars} allowClear/></Form.Item>
                <Form.Item label="Описание" name="description" labelCol={{span: 6}}><Input.TextArea /></Form.Item>
                <Form.Item label="Вход за, мин." name="qtyMinBeforeEnter" labelCol={{span: 6}}><Input /></Form.Item>
              </Col>
              <Col span={8}>
                <div className="room-photo">
                  <img ref={imageRef} src={item?.imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.imageId}.jpg` : "/images/sample.png"} alt="room" className="room-photo-picture" />
                  {id ? <img src="/images/photo.svg" alt="take-photo" className="room-photo-button" onClick={() => setTakePhoto(true)} /> : null}
                  {id ? <img src="/images/upload-photo.svg" alt="upload-photo" className="room-photo-upload-button" onClick={() => setUploadPhoto(true)} /> : null}
                  {id ? <img src="/images/delete-button.svg" alt="delete-photo" className="room-photo-delete-button" onClick={() => onDeletePhoto()} /> : null}
                </div>
              </Col>
            </Row>
          </Form.Item>
          {takePhoto && <TakePhoto onClose={onClosePhoto} onSave={onSavePhoto} />}
          {uploadPhoto && <UploadPhoto onClose={onCloseUpload} onSave={onSavePhoto} />}
          {divProducts}
          {hasSkdLicense ? divCtrls : null}
          <Form.Item label=" ">
            <Space>
              <Button className="sport-btn" onClick={onSave}>Сохранить</Button>
              <LogMonitor vid={10} id={id}/>
              <Button className="sport-btn" onClick={onCancel}>Закрыть</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </div>
  </div>
}

export default Room;

